export default class CreatAudioContext {
  constructor () {
    this._callback = {}
    this.mediaStreamSource = {}
    this.audioContext = {}
    this.meter = {}
    this.rafID = {}
    this.merger1 = {}
    this.merger2 = {}
    this.pannerLeft = {}
    this.pannerRight = {}
    this.splitter = {}
    this.inputNode = {}
    this.gainNodeLeft = {}
    this.gainNodeRight = {}
    this.gainSettings = { // 固定参数
      maxGain: 400,
      maxSliderPosition: 299,
      scalingFactor: 100
    }
    this.xFactor = 0.04093171969123015
    this.slider_position = 239
    this.hasCreateAudioTrack = false
  }

  /**
   *
   * @param {stream} stream from webrtc or rtil
   * @param remoteKey get stream preview id
   * @param callback return data from audioContext
   */
  audioTrack (stream, remoteKey, callback) {
    if (!stream || (stream && !stream.getAudioTracks()[0])) return
    this._callback[remoteKey] = callback
    const audioStream = new MediaStream()
    // 方法会给流添加一个新轨道。指定一个MediaStreamTrack对象作为参数
    audioStream.addTrack(stream.getAudioTracks()[0])
    window.AudioContext = window.AudioContext || window.webkitAudioContext
    this.audioContext[remoteKey] = new AudioContext()
    // mediaStreamSource: AudioContext接口的createMediaStreamSource()方法用于创建一个新的MediaStreamAudioSourceNode 对象,
    // 需要传入一个媒体流对象(MediaStream对象),然后来自MediaStream的音频就可以被播放和操作。
    this.mediaStreamSource[remoteKey] = this.audioContext[
      remoteKey
    ].createMediaStreamSource(audioStream)

    // AudioContext.createChannelMerger()方法，会创建一个ChannelMergerNode，后者可以把多个音频流的通道整合到一个音频流
    this.merger1[remoteKey] = this.audioContext[remoteKey].createChannelMerger(2)
    this.merger2[remoteKey] = this.audioContext[remoteKey].createChannelMerger(2)
    // createGain 可用于控制音频图的整体增益
    // 将一个或多个音频源作为输入，并将输出音量由节点 a-rate 参数指定的级别 增益音频
    // 代表音频相关的参数 a-rate:  获取音频信号的每个采样帧的当前音频参数值
    // k-rate 对于待处理的整个块使用相同的初始音频参数值，即128个采样帧
    this.inputNode[remoteKey] = this.audioContext[remoteKey].createGain()

    // 用于访问音频流的各个通道并分别处理它们
    this.splitter[remoteKey] = this.audioContext[remoteKey].createChannelSplitter(2)

    const pannerOptions = { pan: 0 }
    // StereoPannerNode: 代表创建一个简单的立体声平移节点, 可用于向左或向右平移音频流
    // pan的值是 -1 到 1中的一个无单位的值, 0代表无偏移
    // https://developer.mozilla.org/en-US/docs/Web/API/StereoPannerNode
    this.pannerLeft[remoteKey] = new StereoPannerNode(
      this.audioContext[remoteKey],
      pannerOptions
    )
    this.pannerRight[remoteKey] = new StereoPannerNode(
      this.audioContext[remoteKey],
      pannerOptions
    )

    // connect()方法最明显的用途是将来自一个节点的音频输出引导到另一个节点的音频输入以进行进一步处理
    // https://developer.mozilla.org/zh-CN/docs/Web/API/AudioNode/connect
    this.mediaStreamSource[remoteKey].connect(this.inputNode[remoteKey])

    this.inputNode[remoteKey].connect(this.splitter[remoteKey])
    this.gainNodeLeft[remoteKey] = this.audioContext[remoteKey].createGain()
    this.gainNodeRight[remoteKey] = this.audioContext[remoteKey].createGain()

    this.splitter[remoteKey].connect(this.gainNodeLeft[remoteKey], 0)
    this.splitter[remoteKey].connect(this.gainNodeRight[remoteKey], 1)

    // 静音,将左右声道的声音置为0  取值范围为 0 - 1;
    this.gainNodeLeft[remoteKey].gain.setValueAtTime(0, this.audioContext[remoteKey].currentTime)
    this.gainNodeRight[remoteKey].gain.setValueAtTime(0, this.audioContext[remoteKey].currentTime)
    this.gainNodeLeft[remoteKey].gain.value = 0
    this.gainNodeRight[remoteKey].gain.value = 0
    this.gainNodeLeft[remoteKey].connect(this.merger1[remoteKey], 0, 0)
    this.gainNodeRight[remoteKey].connect(this.merger2[remoteKey], 0, 1)

    this.merger1[remoteKey]
      .connect(this.pannerLeft[remoteKey])
      .connect(this.audioContext[remoteKey].destination)
    this.merger2[remoteKey]
      .connect(this.pannerRight[remoteKey])
      .connect(this.audioContext[remoteKey].destination)

    this.meter[remoteKey] = this.createAudioMeter(this.audioContext[remoteKey])
    this.mediaStreamSource[remoteKey].connect(this.meter[remoteKey])
    this.audioContext[remoteKey].suspend() // suspend audiocontext  because of chrome not allowed audiocontext auto live
    // audioContext[remoteKey].resume();
    this.drawLoop(remoteKey)
    if (!this.hasCreateAudioTrack) {
      this._callback[remoteKey]({ showDialog: true })
      this.hasCreateAudioTrack = true
    }
  }

  drawLoop (remoteKey) {
    const slider_position = this.slider_position
    const outputVolume = this.calculateMeterVolumeForSources(remoteKey)
    if (slider_position && slider_position !== 0) {
      const volumeLeft = outputVolume.outputLeft
      const volumeRight = outputVolume.outputRight
      // condition applied for to set volume as zero, if the volume is in exponential number
      if (volumeLeft.toString().indexOf('e') > -1) {
        outputVolume.outputLeft = 0
      }
      if (volumeRight.toString().indexOf('e') > -1) {
        outputVolume.outputRight = 0
      }
    } else {
      outputVolume.outputLeft = 0
      outputVolume.outputRight = 0
    }
    if (typeof this._callback[remoteKey] === 'function') this._callback[remoteKey](outputVolume)
    window.audioContext = this.audioContext
    this.rafID[remoteKey] = window.requestAnimationFrame(() => this.drawLoop(remoteKey))
  }

  calculateMeterVolumeForSources (remoteKey) {
    const outputVolume = {
      outputLeft: 0,
      outputRight: 0
    }
    const sliderPositionGain = this.getGainInPercentage()
    const meterValue = this.meter[remoteKey] && JSON.parse(JSON.stringify(this.meter[remoteKey]))
    const muterValue1 = meterValue && meterValue.volume[0]
    const muterValue2 = meterValue && meterValue.volume[1]
    // console.log('muterValue1:  muterValue2 : ', muterValue1, muterValue2)
    // const a = {
    //   left: 0,
    //   right: 0
    // }
    // const b = {
    //   left: 0,
    //   right: 0
    // }
    // a.left = sliderPositionGain * (100 - panA) / 100
    // a.right = sliderPositionGain * panA / 100
    //
    // b.left = sliderPositionGain * (100 - panB) / 100
    // b.right = sliderPositionGain * panB / 100
    // outputVolume.outputLeft = a.left * muterValue1 + b.left * muterValue1
    // outputVolume.outputRight = a.right * muterValue2 + b.right * muterValue2
    // const panA = graphQLData.pan[remoteKey].panLeft
    // const panB = graphQLData.pan[remoteKey].panRight

    // 计算公式 gmail地址:
    // https://docs.google.com/document/d/17ENIppV5XbQBSKcN6rBJbuUv6qTNvspTQGLk209bEhM/edit
    const panA = 50 // 这个是左右声道偏移量
    const panB = 50

    const left = sliderPositionGain * (1 + (panA - 50) / 50)
    const right = sliderPositionGain * (1 + (panB - 50) / 50)

    outputVolume.outputLeft = left * muterValue1
    outputVolume.outputRight = right * muterValue2
    return outputVolume
  }

  getGainInPercentage () {
    const floorValue =
      Math.pow(
        10,
        (this.slider_position + this.gainSettings.scalingFactor) / this.gainSettings.scalingFactor
      ) * this.xFactor
    return Math.floor(floorValue) / 100
  }

  createAudioMeter (audioContext, clipLevel, averaging, clipLag) {
    // createScriptProcessor() 方法创建一个ScriptProcessorNode 用于通过JavaScript直接处理音频.
    // 256: bufferSize 缓冲区大小，以样本帧为单位。
    // 具体来讲，缓冲区大小必须是下面这些值当中的某一个:
    // 256, 512, 1024, 2048, 4096, 8192, 16384.
    // 如果不传，或者参数为0，则取当前环境最合适的缓冲区大小,
    // 取值为2的幂次方的一个常数，在该node的整个生命周期中都不变.
    // 该取值控制着audioprocess事件被分派的频率，
    // 以及每一次调用多少样本帧被处理.
    // 较低bufferSzie将导致一定的延迟。
    // 较高的bufferSzie就要注意避免音频的崩溃和故障。
    // 推荐作者不要给定具体的缓冲区大小，让系统自己选一个好的值来平衡延迟和音频质量。
    //
    // window.performance.now() -> 返回一个精确到毫秒的时间戳
    var processor = audioContext.createScriptProcessor(256)
    processor.onaudioprocess = this.volumeAudioProcess
    processor.clipping = false
    processor.lastClip = 0
    processor.volume = [0, 0]
    processor.clipLevel = clipLevel || 0.98
    processor.averaging = averaging || 0.99
    processor.clipLag = clipLag || 750
    processor.connect(audioContext.destination)
    processor.checkClipping = function () {
      if (!this.clipping) return false
      if (this.lastClip + this.clipLag < window.performance.now()) { this.clipping = false }
      return this.clipping
    }
    processor.shutdown = function () {
      this.disconnect()
      this.onaudioprocess = null
    }
    return processor
  }

  volumeAudioProcess (event) {
    var self = this
    var x = 0
    for (var i = 0; i < event.inputBuffer.numberOfChannels; i++) {
      var buf = event.inputBuffer.getChannelData(i)
      var bufLength = buf.length
      for (var j = 0; j < bufLength; j++) {
        var sum = 0
        x = buf[j]
        if (Math.abs(x) >= self.clipLevel) {
          self.clipping = true
          self.lastClip = window.performance.now()
        }
        sum += x * x
      }
      var rms = Math.sqrt(sum / bufLength)
      self.volume[i] = Math.max(rms, self.volume[i] * self.averaging)
    }
  }

  updateSliderPosition (val) {
    // 更新slider position的值 范围为:  0-299
    this.slider_position = Number(val)
  }

  resumeAudioBar () {
    // 将所有的audiocontext对象 变成running状态
    const audioContext = window.audioContext || null
    if (!audioContext) return
    Object.keys(audioContext).map(key => {
      if (audioContext[key] && audioContext[key].state === 'suspended') {
        audioContext[key].resume()
      }
    })
  }
}
